<template>
  <section class="capacity-calendar min-w-0 space-y-4">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h1 class="text-2xl font-semibold text-gray-900">Capacity Calendar</h1>
        <p class="mt-1 text-sm text-gray-500">Review vendor capacity, PO allocations, and remaining availability by period.</p>
      </div>
      <div class="flex items-center gap-2">
        <v-btn density="compact" variant="outlined" @click="scrollToCurrentPeriod">Today</v-btn>
        <template v-if="hasPermission(PERMISSIONS.IMPORT_CAPACITY)">
          <input ref="importFileInput" type="file" accept=".xlsx" class="hidden" @change="onImportFileChange" />
          <button type="button" class="btn-primary" :disabled="store.importParsing || store.approving" @click="triggerImportPicker">
            <span v-if="store.importParsing" class="capacity-calendar__spinner" aria-hidden="true" />
            <span v-else aria-hidden="true">↑</span>
            {{ store.importParsing ? 'Parsing…' : 'Import…' }}
          </button>
        </template>
        <v-btn-toggle v-model="granularity" mandatory density="compact">
          <v-btn value="day">Day</v-btn>
          <v-btn value="week">Week</v-btn>
          <v-btn value="month">Month</v-btn>
        </v-btn-toggle>
      </div>
    </div>

    <div v-if="showCategoryFilter || showVendorFilter" class="flex flex-wrap items-center gap-4 border-b border-gray-200 pb-3">
      <label v-if="showCategoryFilter" class="flex items-center gap-2 text-sm text-gray-600">
        Type
        <FilterSelect v-model="categoryFilterModel" :options="CATEGORY_FILTER_OPTIONS" />
      </label>
      <label v-if="showVendorFilter" class="flex items-center gap-2 text-sm text-gray-600">
        Vendors
        <VendorFilterSelect v-model="vendorFilter" :options="vendorOptions" />
      </label>
    </div>

    <CalendarImportBar
      v-if="importBarProps"
      :file-name="importBarProps.fileName"
      :diff="importBarProps.diff"
      :excluded-count="store.excludedCount"
      :approving="store.approving"
      @discard="onDiscardImport"
      @open-full-import="onOpenFullImport"
      @approve="onApproveImport"
    />

    <CustomerColorLegend />

    <v-alert v-if="error" type="error" :text="error" />
    <v-progress-linear v-else-if="loading" indeterminate color="primary" />

    <div v-else class="capacity-grid-container">
      <div v-if="isCalendarLocked" class="capacity-lock" role="status" aria-live="polite">
        <div class="capacity-lock__card">
          <v-progress-circular indeterminate color="primary" :size="36" :width="3" />
          <h3 class="capacity-lock__title">{{ lockState.title }}</h3>
          <p class="capacity-lock__text">{{ lockState.description }}</p>
          <ul v-if="lockState.vendors.length" class="capacity-lock__vendors">
            <li v-for="vendorName in lockState.vendors" :key="vendorName">{{ vendorName }}</li>
          </ul>
        </div>
      </div>
      <div ref="viewport" class="capacity-grid-viewport" :class="{ 'capacity-grid-viewport--locked': isCalendarLocked }">
        <div class="capacity-grid-canvas" :style="{ width: `${table.getTotalSize()}px` }">
        <table class="capacity-grid">
          <thead class="capacity-grid__header">
            <tr v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id">
              <th
                v-for="header in headerGroup.headers"
                :key="header.id"
                class="capacity-grid__header-cell"
                :class="headerCellClasses(header.column.id)"
                :data-current-period="header.column.id === currentBucketKey"
                :style="columnStyle(header.column, header.getSize())"
              >
                <FlexRender :render="header.column.columnDef.header" :props="header.getContext()" />
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="tableRow in table.getRowModel().rows"
              :key="tableRow.id"
              :class="{ 'capacity-grid__vendor-group-start': tableRow.original.isFirstInVendorGroup, 'capacity-grid__ghost-row': tableRow.original.isGhost }"
            >
              <template v-for="cell in tableRow.getVisibleCells()" :key="cell.id">
                <td
                  v-if="shouldRenderCell(tableRow.original, cell)"
                  class="capacity-grid__cell"
                  :data-allocation-key="`${tableRow.original.id}|${cell.column.id}`"
                  :class="bodyCellClasses(cell)"
                  :rowspan="cell.column.id === 'vendor' ? tableRow.original.vendorRowSpan : undefined"
                  :colspan="cellColumnSpan(tableRow.original.id, cell)"
                  :style="bodyCellStyle(tableRow.original.id, cell)"
                >
                  <FlexRender :render="cell.column.columnDef.cell" :props="cell.getContext()" />
                </td>
              </template>
            </tr>
          </tbody>
        </table>
        </div>
      </div>
    </div>

    <CapacityAllocationDrawer
      v-model="drawerOpen"
      :selection="selectedAllocation"
      :selected-po-number="selectedPoNumber"
      :selected-manual-id="selectedManualId"
      @save-parameters="saveAllocationParameters"
      @select-po="selectPurchaseOrder"
      @select-manual="selectManualAssignment"
      @delete-allocation="deleteSelectedAllocation"
      @remove-assignment="removeSelectedPoAssignment"
    />
  </section>
</template>

<script setup lang="ts">
  import { FlexRender, functionalUpdate, getCoreRowModel, useVueTable, type Cell, type Column, type ColumnDef, type ColumnPinningState } from '@tanstack/vue-table';
  import { storeToRefs } from 'pinia';
  import { computed, h, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
  import { RouterLink, useRoute, useRouter } from 'vue-router';

  import CapacityAllocationDrawer from '@/components/capacity/CapacityAllocationDrawer.vue';
  import CalendarImportBar from '@/components/capacity/CalendarImportBar.vue';
  import CapacityPeriodCell from '@/components/capacity/CapacityPeriodCell.vue';
  import CustomerColorLegend from '@/components/capacity/CustomerColorLegend.vue';
  import CapacityTypePill from '@/components/capacity/CapacityTypePill.vue';
  import CurrentPeriodTag from '@/components/capacity/CurrentPeriodTag.vue';
  import VendorFilterSelect from '@/components/capacity/VendorFilterSelect.vue';
  import FilterSelect from '@/components/common/FilterSelect.vue';
  import { useAccess } from '@/composables/useAccess';
  import type { DropResolution } from '@/composables/useCalendarDragDrop';
  import { useCapacityCalendar, type DisplayGranularity } from '@/composables/useCapacityCalendar';
  import { useNoPlanMenu } from '@/composables/useNoPlanMenu';
  import { fetchCapacityTypes } from '@/services/capacityTypeService';
  import { useCapacityCalendarStore } from '@/store/capacityCalendarStore';
  import { useCustomerMappingStore } from '@/store/customerMappingStore';
  import { PERMISSIONS } from '@/types/access';
  import type { AllocationParametersUpdate, CalendarEntry, CalendarRow, SelectedAllocation } from '@/types/capacityCalendar';
  import type { CapacityCategory, CapacityType } from '@/types/capacityType';
  import { buildBlockOrder, groupCalendarRows } from '@/utils/capacityCalendar';
  import type { PendingCell } from '@/utils/importPreview';

  const store = useCapacityCalendarStore();
  const customerMappingStore = useCustomerMappingStore();
  const { hasAllVendors, hasPermission } = useAccess();
  const route = useRoute();
  const router = useRouter();
  const { allocations, error, loading, rows } = storeToRefs(store);
  const granularity = ref<DisplayGranularity>('week');
  const viewport = ref<HTMLElement | null>(null);

  // Vendors currently locked by an in-flight import confirm — this browser's own
  // (store.approving) or, once this page's next load/refresh picks it up, someone
  // else's (store.lockedVendorIds). See the plan's Global Constraints for why this
  // isn't polled for live freshness.
  const lockedVendorNames = computed(() =>
    Array.from(new Set(store.rows.filter((row) => row.vendorId && store.lockedVendorIds.includes(row.vendorId)).map((row) => row.vendorName))),
  );
  const isCalendarLocked = computed(() => store.importParsing || store.approving || lockedVendorNames.value.length > 0);
  // Same three states as before, split into a title and a supporting line so the overlay can
  // say which one you are in at a glance; only the third names vendors, because the first two
  // are this browser's own in-flight work.
  const lockState = computed<{ title: string; description: string; vendors: string[] }>(() => {
    if (store.importParsing) {
      return {
        title: 'Parsing your capacity file',
        description: 'Reading the workbook and matching its vendors and capacity types.',
        vendors: [],
      };
    }
    if (store.approving) {
      return {
        title: 'Applying import changes',
        description: 'Writing the new capacity plan — keep this tab open until it finishes.',
        vendors: [],
      };
    }
    return {
      title: 'Import in progress',
      description: 'Another import is writing capacity for these vendors. Edits unlock once it finishes.',
      vendors: lockedVendorNames.value,
    };
  });
  const columnPinning = ref<ColumnPinningState>({ left: ['vendor', 'termination'] });

  // Single-category tenants (and partners without molding) never see this control —
  // it only appears once the loaded rows actually span more than one category.
  // Computed once per data change rather than per cell: the calendar renders every row against
  // every column, and walking the rows inside each cell would be quadratic.
  const blockOrder = computed(() => buildBlockOrder(rows.value, allocations.value));

  const showCategoryFilter = computed(() => new Set(rows.value.map((row) => row.category)).size > 1);
  const categoryFilter = ref<CapacityCategory | 'all'>('all');
  const CATEGORY_FILTER_OPTIONS = [
    { value: 'all', label: 'All types' },
    { value: 'termination', label: 'Termination' },
    { value: 'molding', label: 'Molding' },
  ];
  // FilterSelect works in plain strings; this narrows them back to the union type.
  const categoryFilterModel = computed({
    get: () => categoryFilter.value as string,
    set: (value: string) => {
      categoryFilter.value = value as CapacityCategory | 'all';
    },
  });

  // Vendor multi-select: options derive from the loaded rows (inherently partner-scoped),
  // an empty selection means "all vendors" so clearing never blanks the calendar, and the
  // control hides when only one vendor exists — same convention as the category filter.
  const vendorFilter = ref<string[]>([]);
  const vendorOptions = computed(() => {
    const byCode = new Map<string, string>();
    rows.value.forEach((row) => {
      if (!byCode.has(row.vendorCode)) byCode.set(row.vendorCode, row.vendorName || row.vendorCode);
    });
    return [...byCode.entries()].map(([code, name]) => ({ code, name }));
  });
  // Always visible for all-vendor callers (their vendor list grows over time); scoped callers
  // only see it when they actually have multiple vendors on the calendar (e.g. WeSum+ZeSum accounts).
  const showVendorFilter = computed(() => hasAllVendors.value || vendorOptions.value.length > 1);

  // Capacity types, fetched lazily the first time an import preview proposes a ghost row —
  // resolves the ghost's real type label/category/unit instead of showing the bare parsed
  // code. Best-effort: a failed fetch just leaves ghost rows on the typeCode fallback.
  const capacityTypes = ref<CapacityType[]>([]);
  const capacityTypeById = computed(() => new Map(capacityTypes.value.map((type) => [type.id, type])));
  let capacityTypesRequested = false;
  async function ensureCapacityTypesLoaded(): Promise<void> {
    if (capacityTypesRequested) return;
    capacityTypesRequested = true;
    try {
      capacityTypes.value = await fetchCapacityTypes();
    } catch {
      capacityTypes.value = [];
    }
  }
  // fileName + diff bundled together so the template only needs one non-null guard — pendingDiff
  // is derived from pendingImport (see the store), so the two are only ever both-null or
  // both-set, but they're independent computeds and TS can't infer that on its own.
  const importBarProps = computed(() => {
    const pending = store.pendingImport;
    const diff = store.pendingDiff;
    return pending && diff ? { fileName: pending.fileName, diff } : null;
  });

  watch(
    () => store.pendingDiff?.ghostRows.length ?? 0,
    (count) => {
      if (count > 0) void ensureCapacityTypesLoaded();
    },
    { immediate: true },
  );

  // Synthetic rows for import-preview lines whose vendor+type has no calendar row yet
  // (see buildImportDiff's ghostRows) — filtered by the same category filter as real rows
  // so a molding-only view doesn't surface a termination ghost row, and vice versa.
  const ghostEntries = computed<CalendarEntry[]>(() =>
    (store.pendingDiff?.ghostRows ?? []).flatMap((ghost) => {
      const type = capacityTypeById.value.get(ghost.capacityTypeId);
      const category: CapacityCategory = type?.category ?? 'termination';
      if (categoryFilter.value !== 'all' && categoryFilter.value !== category) return [];
      // The ghost's vendorId is guaranteed to already have real rows on the calendar
      // (buildImportDiff only proposes a ghost row for a known portal vendor) — prefer
      // that row's vendorCode/vendorName so groupCalendarRows places the ghost row inside
      // the vendor's existing group instead of starting a stray one on a stale/mistyped code.
      const realVendorRow = rows.value.find((row) => row.vendorId === ghost.vendorId);
      const entry: CalendarEntry = {
        id: ghost.rowId,
        vendorId: ghost.vendorId,
        capacityTypeId: ghost.capacityTypeId,
        vendorCode: realVendorRow?.vendorCode ?? ghost.vendorCode,
        vendorName: realVendorRow?.vendorName ?? ghost.vendorCode,
        typeCode: ghost.typeCode,
        typeLabel: type?.label ?? ghost.typeCode,
        category,
        unit: type?.unit ?? null,
        isGhost: true,
      };
      return [entry];
    }),
  );

  // Re-group after filtering (and after appending ghost rows): isFirstInVendorGroup/
  // vendorRowSpan were stamped on the full row set, so a filtered-out first row would
  // otherwise hide its vendor's name cell. Ghost rows are appended last so they render
  // after their vendor's real rows within the group.
  const filteredRows = computed(() => {
    const matchesFilters = (row: { category: CapacityCategory; vendorCode: string }): boolean =>
      (categoryFilter.value === 'all' || row.category === categoryFilter.value) &&
      (vendorFilter.value.length === 0 || vendorFilter.value.includes(row.vendorCode));
    return groupCalendarRows([...rows.value.filter(matchesFilters), ...ghostEntries.value.filter(matchesFilters)]);
  });

  // Pending-import overlay, keyed by `${rowId}:${weekStart}` — empty outside an active preview.
  const pendingCellMap = computed<Map<string, PendingCell>>(() => {
    const map = new Map<string, PendingCell>();
    store.pendingDiff?.cells.forEach((cell) => {
      map.set(`${cell.rowId}:${cell.weekStart}`, cell);
    });
    return map;
  });

  // Matched-import marker, keyed the same way as pendingCellMap — a week whose file value
  // already equals the calendar's (see MatchedCell). Disjoint from pendingCellMap by
  // construction, but CapacityPeriodCell still resolves any (impossible) overlap in pending's
  // favor.
  const matchedCellMap = computed<Map<string, true>>(() => {
    const map = new Map<string, true>();
    store.pendingDiff?.matchedCells.forEach((cell) => {
      map.set(`${cell.rowId}:${cell.weekStart}`, true);
    });
    return map;
  });

  // Centring a row means half its viewport's height above it.
  const HALF = 2;
  // What the tag on the current column says, per view.
  const CURRENT_PERIOD_LABEL: Record<DisplayGranularity, string> = { day: 'Today', week: 'This week', month: 'This month' };
  const VENDOR_COLUMN_WIDTH = 72;
  const TERMINATION_COLUMN_WIDTH = 108;
  const PERIOD_COLUMN_WIDTH: Record<DisplayGranularity, number> = {
    day: 88,
    week: 280,
    month: 220,
  };

  const { buckets, currentBucketKey, getBucketForColumn, getCellDisplay, getPendingDisplay, getMatchedDisplay } = useCapacityCalendar(
    granularity,
    filteredRows,
    allocations,
    pendingCellMap,
    matchedCellMap,
  );
  const selectedPoNumber = computed(() => {
    const queryValue = route.query.po;
    return Array.isArray(queryValue) ? (queryValue[0] ?? null) : (queryValue ?? null);
  });
  const selectedManualId = computed(() => {
    const queryValue = route.query.manual;
    return Array.isArray(queryValue) ? (queryValue[0] ?? null) : (queryValue ?? null);
  });
  const selectedAllocationKey = computed(() => {
    const queryValue = route.query.allocation;
    return Array.isArray(queryValue) ? (queryValue[0] ?? null) : (queryValue ?? null);
  });
  // The deep link (?manual=) stays keyed by the assignment id the drawer needs, but a
  // grouped manual block highlights as a whole: its shared distributionGroupId is the key
  // the sibling segments in the other type rows also carry.
  const highlightedManualKey = computed(() => {
    const manualId = selectedManualId.value;
    if (!manualId) return null;
    const assignment = Object.values(allocations.value)
      .flatMap((byWeek) => Object.values(byWeek))
      .flatMap((allocation) => allocation.purchaseOrders)
      .find((po) => po.id === manualId);
    return assignment?.distributionGroupId ?? manualId;
  });
  const selectedWeekKey = computed(() => selectedAllocationKey.value?.split('|')[1] ?? null);

  const selectedAllocation = computed<SelectedAllocation | null>(() => {
    if (!selectedAllocationKey.value) {
      return null;
    }

    const [rowId, weekKey] = selectedAllocationKey.value.split('|');
    const row = rows.value.find((candidate) => candidate.id === rowId);
    const allocation = allocations.value[rowId]?.[weekKey];
    return row && allocation
      ? {
          allocation,
          rowId,
          vendorName: row.vendorName,
          typeLabel: row.typeLabel,
          vendorId: row.vendorId,
          capacityTypeId: row.capacityTypeId,
          typeCode: row.typeCode,
          category: row.category,
        }
      : null;
  });
  const drawerOpen = computed({
    get: () => selectedAllocation.value !== null,
    set: (open: boolean) => {
      if (!open) {
        void clearPurchaseOrderSelection();
      }
    },
  });
  const columns = computed<ColumnDef<CalendarRow>[]>(() => [
    {
      id: 'vendor',
      header: 'Vendor',
      // Alias as the label, full vendor name on hover. Links only when the caller can open
      // the vendor page (read:vendors), so nobody is sent to Forbidden.
      cell: ({ row }) =>
        hasPermission(PERMISSIONS.READ_VENDORS) && row.original.vendorId
          ? h(
              RouterLink,
              { to: { name: 'VendorDetail', params: { id: row.original.vendorId } }, title: row.original.vendorName, class: 'capacity-grid__vendor-link' },
              () => row.original.vendorCode,
            )
          : h('span', { title: row.original.vendorName }, row.original.vendorCode),
      size: VENDOR_COLUMN_WIDTH,
    },
    {
      id: 'termination',
      header: 'Type',
      cell: ({ row }) =>
        row.original.isGhost
          ? h('div', { class: 'capacity-grid__ghost-type' }, [
              h(CapacityTypePill, { category: row.original.category, label: row.original.typeLabel }),
              h('span', { class: 'capacity-grid__ghost-hint' }, 'will be added by this import'),
            ])
          : h(CapacityTypePill, { category: row.original.category, label: row.original.typeLabel }),
      size: TERMINATION_COLUMN_WIDTH,
    },
    ...buckets.value.map<ColumnDef<CalendarRow>>((bucket) => ({
      id: bucket.key,
      header: () =>
        h('span', { class: 'flex w-full items-center gap-1.5' }, [
          h('span', bucket.label),
          bucket.isCurrent ? h(CurrentPeriodTag, { label: CURRENT_PERIOD_LABEL[granularity.value] }) : null,
        ]),
      size: PERIOD_COLUMN_WIDTH[granularity.value],
      cell: ({ row }) => {
        const display = getCellDisplay(row.original.id, bucket.key);
        // Drag/drop is meaningless on a month rollup or single-day view — gated
        // the same way as weekKey below. A ghost row is never a real drag source/target
        // either — it has no allocation yet, only a pending overlay (or nothing).
        const isWeekView = granularity.value === 'week';
        const isInteractive = isWeekView && !row.original.isGhost;
        const pending = getPendingDisplay(row.original.id, bucket.key);
        // A cell can't be both pending and matched by construction (buildImportDiff puts a
        // week in exactly one of cells/matchedCells) — pending wins if it somehow happens anyway.
        const matched = !pending && getMatchedDisplay(row.original.id, bucket.key);
        // No-plan micro-menu (spec §4.3): week view + write:capacity only; a pending-import
        // ghost cell is inert and a non-portal-linked row can't be edited. Allocated cells are
        // never inline-editable — clicking anywhere on them opens the drawer (selectAllocation).
        const canOpenMenu = isInteractive && !pending && hasPermission(PERMISSIONS.WRITE_CAPACITY);
        // Whether the "No plan" cell does ANYTHING when clicked — either the micro-menu (week +
        // write:capacity) or, at month granularity, drilling into week view (unrelated to
        // permission — see onCreateAllocation below). Everything else (day view; week view
        // without write:capacity) renders display-only per review finding #3.
        const hasNoPlanAction = !row.original.isGhost && !pending && (granularity.value === 'month' || canOpenMenu);
        const menuOpenThis = canOpenMenu && noPlanMenu.isMenuCell(row.original.id, bucket.key);
        return h(CapacityPeriodCell, {
          allocation: display.allocation,
          columnSpan: display.columnSpan,
          selected: selectedAllocationKey.value === `${row.original.id}|${bucket.key}`,
          // Only meaningful at week granularity — a month bucket's key is
          // also a YYYY-MM-DD string (the 1st of the month) and can
          // string-collide with an unrelated week's recentAssignments.weekKeys,
          // spuriously flashing/dimming a month rollup cell.
          weekKey: isWeekView ? bucket.key : undefined,
          typeCode: row.original.typeCode,
          unit: row.original.unit,
          rowId: isInteractive ? row.original.id : undefined,
          vendorId: isInteractive ? row.original.vendorId : undefined,
          capacityTypeId: isInteractive ? row.original.capacityTypeId : undefined,
          // Rings every segment (in every row) whose key matches the drawer's current
          // selection — a poNumber, or the manual block's group-aware key, never both at
          // once (see selectedPoNumber/selectedManualId above).
          highlightedSegmentKey: selectedPoNumber.value ?? highlightedManualKey.value,
          // The ring belongs to the week the selection was actually made in; the same PO in
          // other weeks would otherwise look like several separate selections. Month rollups
          // carry no weekKey (see above), so they keep ringing every match.
          highlightedAnchorWeekKey: isWeekView ? selectedWeekKey.value : null,
          // Shared by every row in this vendor's column, so a distributed block lines up.
          orderKeys: blockOrder.value.get(`${row.original.vendorCode}|${bucket.key}`),
          pending,
          matched,
          excludeDisabled: store.approving,
          editable: hasNoPlanAction,
          menuOpen: menuOpenThis,
          previousWeekPlan: menuOpenThis ? noPlanMenu.previousWeekPlan(row.original.id, bucket.key) : null,
          // Month view is a read-only rollup — clicking drills into the week view. Every other
          // click on an allocated cell opens the drawer, exactly as it did before Task 3.
          onSelectAllocation: () => {
            if (row.original.isGhost) return;
            if (granularity.value === 'month') drillIntoWeeks();
            else void selectAllocation(row.original.id, bucket.key);
          },
          // Clicking a specific PO segment opens the drawer on that PO directly, same as
          // onSelectAllocation but with ?po= set in the same navigation (see
          // onSelectAllocationPo below) so the drawer never flashes an unselected state first.
          onSelectPo: (poNumber: string) => {
            if (row.original.isGhost) return;
            if (granularity.value === 'month') drillIntoWeeks();
            else void onSelectAllocationPo(row.original.id, bucket.key, poNumber);
          },
          onSelectManual: (assignmentId: string) => {
            if (row.original.isGhost) return;
            if (granularity.value === 'month') drillIntoWeeks();
            else void onSelectAllocationManual(row.original.id, bucket.key, assignmentId);
          },
          // The create dialog is gone — clicking "No plan" now only does something when the
          // cell can actually open the menu (week view + write:capacity) or drill into weeks
          // (month view); otherwise the cell renders display-only (see hasNoPlanAction above).
          onCreateAllocation: () => {
            if (row.original.isGhost) return;
            if (granularity.value === 'month') {
              drillIntoWeeks();
              return;
            }
            if (canOpenMenu) noPlanMenu.openMenu(row.original.id, bucket.key);
          },
          onMoveDropped: isInteractive ? (resolution: DropResolution) => handleMoveDropped(resolution) : undefined,
          onCloseMenu: () => noPlanMenu.closeMenu(row.original.id, bucket.key),
          onCopyPreviousWeek: () => void noPlanMenu.copyPreviousWeek(row.original.id, bucket.key),
          onAddCapacity: (capacity: number) => void noPlanMenu.addCapacity(row.original.id, bucket.key, capacity),
          // The pending cell itself carries its rowId/weekStart (see PendingCell), so this works
          // for ghost rows too, whose rowId prop above is deliberately left undefined.
          onExcludeCell: (rowId: string, weekStart: string) => store.excludePendingCell(rowId, weekStart),
        });
      },
    })),
  ]);

  const table = useVueTable({
    get data() {
      return filteredRows.value;
    },
    get columns() {
      return columns.value;
    },
    getCoreRowModel: getCoreRowModel(),
    enableColumnPinning: true,
    state: {
      get columnPinning() {
        return columnPinning.value;
      },
    },
    onColumnPinningChange: (updater) => {
      columnPinning.value = functionalUpdate(updater, columnPinning.value);
    },
  });

  function headerCellClasses(columnId: string): Record<string, boolean> {
    const column = table.getColumn(columnId);
    return {
      'capacity-grid__sticky-column': Boolean(column?.getIsPinned()),
      'capacity-grid__corner': Boolean(column?.getIsPinned()),
      'capacity-grid__termination-column': columnId === 'termination',
      'capacity-grid__day-header': granularity.value === 'day' && columnId !== 'vendor' && columnId !== 'termination',
    };
  }

  function columnStyle(column: Column<CalendarRow>, size: number): Record<string, string> {
    const style: Record<string, string> = {
      width: `${size}px`,
      minWidth: `${size}px`,
    };

    if (column.getIsPinned() === 'left') {
      style.left = `${column.getStart('left')}px`;
    }

    return style;
  }

  function shouldRenderCell(row: CalendarRow, cell: Cell<CalendarRow, unknown>): boolean {
    if (cell.column.id === 'vendor') {
      return row.isFirstInVendorGroup;
    }

    const bucket = getBucketForColumn(cell.column.id);
    return !bucket || !getCellDisplay(row.id, bucket.key).hidden;
  }

  function cellColumnSpan(rowId: string, cell: Cell<CalendarRow, unknown>): number | undefined {
    const bucket = getBucketForColumn(cell.column.id);
    return bucket ? getCellDisplay(rowId, bucket.key).columnSpan : undefined;
  }

  function bodyCellClasses(cell: Cell<CalendarRow, unknown>): Record<string, boolean> {
    return {
      'capacity-grid__sticky-column': Boolean(cell.column.getIsPinned()),
      'capacity-grid__vendor-label': cell.column.id === 'vendor',
      'capacity-grid__row-label': cell.column.id === 'termination',
      'capacity-grid__termination-column': cell.column.id === 'termination',
      'capacity-grid__cell--current': cell.column.id === currentBucketKey.value,
    };
  }

  function bodyCellStyle(rowId: string, cell: Cell<CalendarRow, unknown>): Record<string, string> {
    const bucket = getBucketForColumn(cell.column.id);
    const span = bucket ? getCellDisplay(rowId, bucket.key).columnSpan : 1;
    const size = cell.column.getSize() * span;
    return columnStyle(cell.column, size);
  }

  // A link from the scheduling board names a cell, not a date. Landing on today instead
  // leaves the thing it highlighted off-screen — a different week horizontally, and often a
  // different vendor's row vertically. Returns false when the cell is not on this grid (a
  // stale link, or a granularity whose columns are not weeks) so the caller can fall back.
  async function scrollToDeepLinkedAllocation(): Promise<boolean> {
    const key = selectedAllocationKey.value;
    if (!key) {
      return false;
    }
    await nextTick();
    const cell = viewport.value?.querySelector<HTMLElement>(`[data-allocation-key="${key}"]`);
    if (!viewport.value || !cell) {
      return false;
    }

    const pinnedWidth = VENDOR_COLUMN_WIDTH + TERMINATION_COLUMN_WIDTH;
    viewport.value.scrollLeft = Math.max(cell.offsetLeft - pinnedWidth, 0);
    // Centred rather than flush to the top: the header is sticky, and a row pinned right
    // under it reads as cut off.
    viewport.value.scrollTop = Math.max(cell.offsetTop - viewport.value.clientHeight / HALF, 0);
    return true;
  }

  async function scrollToCurrentPeriod(): Promise<void> {
    await nextTick();
    const currentHeader = viewport.value?.querySelector<HTMLElement>('[data-current-period="true"]');
    if (!viewport.value || !currentHeader) {
      return;
    }

    const pinnedWidth = VENDOR_COLUMN_WIDTH + TERMINATION_COLUMN_WIDTH;
    viewport.value.scrollLeft = Math.max(currentHeader.offsetLeft - pinnedWidth, 0);
  }

  async function selectPurchaseOrder(poNumber: string): Promise<void> {
    const query = { ...route.query };
    delete query.manual;
    await router.replace({
      query: {
        ...query,
        po: poNumber,
      },
    });
  }

  async function selectManualAssignment(assignmentId: string): Promise<void> {
    const query = { ...route.query };
    delete query.po;
    await router.replace({
      query: {
        ...query,
        manual: assignmentId,
      },
    });
  }

  function drillIntoWeeks(): void {
    granularity.value = 'week';
  }

  async function selectAllocation(rowId: string, weekKey: string): Promise<void> {
    const query = { ...route.query };
    delete query.po;
    delete query.manual;
    await router.replace({
      query: {
        ...query,
        allocation: `${rowId}|${weekKey}`,
      },
    });
  }

  // Clicking a PO segment directly (see CapacityAllocationBar's selectPo) selects both the
  // allocation and that PO in one navigation — two chained router.replace calls would flash
  // the drawer open with no PO selected before the second replace lands.
  async function onSelectAllocationPo(rowId: string, weekKey: string, poNumber: string): Promise<void> {
    const query = { ...route.query };
    delete query.manual;
    await router.replace({
      query: {
        ...query,
        allocation: `${rowId}|${weekKey}`,
        po: poNumber,
      },
    });
  }

  // Same single-navigation shape as onSelectAllocationPo, but for a manual block —
  // identified by its assignment id since a manual block may have no PO number.
  async function onSelectAllocationManual(rowId: string, weekKey: string, assignmentId: string): Promise<void> {
    const query = { ...route.query };
    delete query.po;
    await router.replace({
      query: {
        ...query,
        allocation: `${rowId}|${weekKey}`,
        manual: assignmentId,
      },
    });
  }

  async function clearPurchaseOrderSelection(): Promise<void> {
    const query = { ...route.query };
    delete query.po;
    delete query.manual;
    delete query.allocation;
    await router.replace({ query });
  }

  const noPlanMenu = useNoPlanMenu();

  const importFileInput = ref<HTMLInputElement | null>(null);

  function triggerImportPicker(): void {
    importFileInput.value?.click();
  }

  async function onImportFileChange(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    // Reset immediately so picking the same file again still fires @change.
    input.value = '';
    if (!file) return;
    await store.startImportPreview(file);
  }

  function onDiscardImport(): void {
    store.discardPendingImport();
  }

  async function onApproveImport(): Promise<void> {
    await store.approvePendingImport();
  }

  // Hands the parse off to the full import page for reconciliation (unresolved vendors/types) —
  // the pending preview is discarded either way, matching "starting a new import replaces it".
  async function onOpenFullImport(): Promise<void> {
    const importRecordId = store.pendingImport?.parse.importRecordId;
    store.discardPendingImport();
    await router.push(importRecordId ? { name: 'CapacityImport', query: { record: importRecordId } } : { name: 'CapacityImport' });
  }

  async function saveAllocationParameters(update: AllocationParametersUpdate): Promise<void> {
    if (selectedAllocation.value) {
      await store.updateAllocationParameters(selectedAllocation.value.rowId, selectedAllocation.value.allocation.weekKey, update);
    }
  }

  async function deleteSelectedAllocation(): Promise<void> {
    if (!selectedAllocation.value) return;
    const removed = await store.deleteAllocation(selectedAllocation.value.rowId, selectedAllocation.value.allocation.weekKey);
    if (removed) await clearPurchaseOrderSelection();
  }

  async function removeSelectedPoAssignment(assignmentId: string): Promise<void> {
    if (!selectedAllocation.value) return;
    await store.removePoAssignment(selectedAllocation.value.rowId, selectedAllocation.value.allocation.weekKey, assignmentId);
  }

  // DropResolution.rowId is the drop target's row and .weekKey is the drag's
  // ORIGIN week (see useCalendarDragDrop.completeDrop) — for an 'assignment'
  // drop, isCompatibleTarget already required the same vendorId+capacityTypeId
  // as the source, so the row is invariant between source and target and
  // resolution.rowId doubles as the lookup key for the allocation being moved
  // from. For a 'group' drop, moveDistributionGroup only uses rowId to resolve
  // the initiating row's vendorId for patching — any member row works.
  async function handleMoveDropped(resolution: DropResolution): Promise<void> {
    if (resolution.kind === 'assignment') {
      await store.moveAssignment(resolution.rowId, resolution.weekKey, resolution.assignmentId!, resolution.targetWeekStart);
    } else {
      await store.moveDistributionGroup(resolution.rowId, resolution.distributionGroupId!, resolution.targetWeekStart);
    }
  }

  // Closing/reloading the tab mid-import would abandon the process: a parse or confirm still
  // in flight, or an unapproved preview the user would have to re-import. Browsers ignore
  // custom text here — registering the handler only triggers the native "unsaved changes"
  // confirmation, which is the strongest warning the platform allows.
  function warnBeforeUnload(event: BeforeUnloadEvent): void {
    if (!store.importParsing && !store.approving && !store.pendingImport) return;
    event.preventDefault();
  }

  onMounted(async () => {
    window.addEventListener('beforeunload', warnBeforeUnload);
    // Non-blocking: a failed load simply leaves all segments gray.
    if (!customerMappingStore.loaded) {
      void customerMappingStore.load();
    }
    await store.load();
    if (!(await scrollToDeepLinkedAllocation())) {
      await scrollToCurrentPeriod();
    }
  });

  onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', warnBeforeUnload);
  });

  watch(granularity, () => {
    void scrollToCurrentPeriod();
    // The micro-menu only exists at week granularity — leaving week view (even briefly, e.g.
    // Week→Month→Week) must not leave a stale menu open on re-entry.
    noPlanMenu.closeAny();
  });
</script>

<style scoped>
  .capacity-calendar {
    width: 100%;
    max-width: 100%;
  }

  .capacity-grid-container {
    position: relative;
  }

  .capacity-lock {
    position: absolute;
    inset: 0;
    z-index: 50;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
    /* Matches the viewport's radius so the wash does not square off its rounded corners. */
    border-radius: 0.75rem;
    background: rgb(248 250 252 / 72%);
    backdrop-filter: blur(3px);
  }

  .capacity-lock__card {
    display: flex;
    max-width: 26rem;
    flex-direction: column;
    align-items: center;
    gap: 0.625rem;
    padding: 1.75rem 2rem;
    border: 1px solid rgb(226 232 240);
    border-radius: 0.75rem;
    background: white;
    box-shadow: 0 10px 30px rgb(15 23 42 / 12%);
    text-align: center;
  }

  .capacity-lock__title {
    margin: 0.25rem 0 0;
    color: rgb(15 23 42);
    font-size: 1rem;
    font-weight: 600;
  }

  .capacity-lock__text {
    margin: 0;
    color: rgb(100 116 139);
    font-size: 0.8125rem;
    line-height: 1.5;
  }

  .capacity-lock__vendors {
    display: flex;
    margin: 0.25rem 0 0;
    padding: 0;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.375rem;
    list-style: none;
  }

  .capacity-lock__vendors li {
    padding: 0.125rem 0.625rem;
    border-radius: 9999px;
    background: rgb(241 245 249);
    color: rgb(51 65 85);
    font-size: 0.75rem;
    font-weight: 600;
  }

  .capacity-grid-viewport {
    width: 100%;
    max-height: calc(100vh - 12rem);
    overflow: auto;
    border: 1px solid rgb(209 213 219);
    border-radius: 0.75rem;
    background: white;
    box-shadow: 0 1px 3px rgb(15 23 42 / 8%);
  }

  .capacity-grid-viewport--locked {
    pointer-events: none;
  }

  .capacity-calendar__spinner {
    display: inline-block;
    width: 0.875rem;
    height: 0.875rem;
    border: 2px solid rgb(255 255 255 / 45%);
    border-top-color: white;
    border-radius: 9999px;
    animation: capacity-calendar-spin 700ms linear infinite;
  }

  @keyframes capacity-calendar-spin {
    to {
      transform: rotate(360deg);
    }
  }

  .capacity-grid {
    position: relative;
    /* table-layout: fixed only engages with a non-auto width; without it the
       current week's crowded cells widen their column past the declared 280px */
    width: 100%;
    min-width: 100%;
    table-layout: fixed;
    border-spacing: 0;
    border-collapse: separate;
  }

  .capacity-grid-canvas {
    position: relative;
    min-width: 100%;
  }

  .capacity-grid__header {
    position: sticky;
    top: 0;
    z-index: 20;
  }

  .capacity-grid__header-cell,
  .capacity-grid__cell {
    border-right: 1px solid rgb(226 232 240);
    border-bottom: 1px solid rgb(226 232 240);
    text-align: left;
    vertical-align: middle;
    white-space: nowrap;
  }

  .capacity-grid__header-cell {
    height: 48px;
    padding: 0.75rem;
    background: rgb(248 250 252);
    color: rgb(71 85 105);
    font-size: 0.75rem;
    font-weight: 600;
  }

  /* The column the user is standing in, marked exactly as the scheduling board marks it —
     the two views are read side by side, and a red rule down one of them was a third
     vocabulary for the same fact. */
  .capacity-grid__header-cell[data-current-period='true'] {
    background: rgb(239 246 255);
    color: rgb(29 78 216);
  }

  .capacity-grid__cell--current:not(.capacity-grid__vendor-label) {
    background: rgb(239 246 255 / 45%);
  }

  .capacity-grid__day-header {
    height: auto;
    min-height: 48px;
    padding: 0.5rem 0.25rem;
    text-align: center;
    line-height: 1.2;
    white-space: pre-line;
  }

  .capacity-grid__cell {
    height: 80px;
    padding: 0.5rem 0.75rem;
    background: white;
    color: rgb(51 65 85);
    font-size: 0.875rem;
  }

  .capacity-grid__vendor-label {
    font-weight: 700;
    text-align: center;
    vertical-align: middle;
    background: white;
  }

  .capacity-grid__vendor-label :deep(.capacity-grid__vendor-link) {
    color: rgb(29 78 216);
    text-decoration: none;
  }

  .capacity-grid__vendor-label :deep(.capacity-grid__vendor-link:hover) {
    text-decoration: underline;
  }

  /* Skips the current column: striping is far more specific than the highlight, so without
     this it would erase it on every other row. */
  .capacity-grid tbody tr:nth-child(even) .capacity-grid__cell:not(.capacity-grid__vendor-label):not(.capacity-grid__cell--current) {
    background: rgb(248 250 252 / 65%);
  }

  .capacity-grid tbody tr:hover .capacity-grid__cell:not(.capacity-grid__vendor-label) {
    background: rgb(239 246 255);
  }

  .capacity-grid__vendor-group-start .capacity-grid__cell {
    border-top: 2px solid rgb(203 213 225);
  }

  /* Import-preview ghost row (see buildImportDiff's ghostRows): a faint amber wash marks
     the whole row as not-yet-real, on top of the per-cell pending overlay. */
  .capacity-grid__ghost-row .capacity-grid__cell:not(.capacity-grid__vendor-label) {
    background: rgb(255 251 235 / 60%);
  }

  .capacity-grid__ghost-type {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.125rem;
  }

  .capacity-grid__ghost-hint {
    font-size: 0.625rem;
    font-style: italic;
    color: rgb(146 64 14);
  }

  .capacity-grid tbody tr:first-child .capacity-grid__cell {
    border-top: 0;
  }

  .capacity-grid__sticky-column {
    position: sticky;
    z-index: 10;
    box-shadow: 1px 0 0 rgb(203 213 225);
  }

  .capacity-grid__corner {
    z-index: 30;
  }

  .capacity-grid__row-label {
    font-weight: 600;
  }

  .capacity-grid__termination-column {
    border-left: 1px solid rgb(203 213 225);
  }

  .capacity-grid__header-cell:last-child,
  .capacity-grid__cell:last-child {
    border-right: 0;
  }

  .capacity-grid tbody tr:last-child .capacity-grid__cell {
    border-bottom: 0;
  }

  @media (max-width: 640px) {
    .capacity-grid-viewport {
      max-height: calc(100vh - 14rem);
    }
  }
</style>
